import { useMemo, useState, Fragment } from 'react';
import {
  collection, doc, runTransaction,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { usePaging } from '../hooks/usePaging';
import { Paging, DeleteIconButton } from '../components/ui';
import { Button, Modal, ConfirmModal } from '../components/ui';
import { useSettings } from '../hooks/useSettings';
import { useLedgerData } from '../hooks/useLedgerData';
import { DateRangeButton, IconPlusSm } from '../components/HeaderControls';
import { inWindow, computeDateWindow, TYPE_LABEL, type DateFilterKey } from '../utils/ledgerReports';
import { buildEntry } from '../utils/ledger';
import { fmtMoney } from '../utils/currency';
import type { JournalEntry, LedgerAccount, LedgerAccountType } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const SRC_LABEL: Record<string, string> = {
  purchase_invoice: 'Purchase', sales_invoice: 'Sale',
  ap_payment: 'Supplier payment', ar_payment: 'Customer receipt',
  broker_accrual: 'Broker commission', broker_payment: 'Broker payout',
  journal: 'Journal entry', expense: 'Expense', income: 'Income',
  opening_balance: 'Opening balance', opening_stock: 'Opening stock',
  purchase_expense: 'Purchase expense', sale_expense: 'Sales expense',
  purchase_return: 'Purchase return', sales_return: 'Credit note', purchase_discount: 'Purchase discount', sale_settle: 'Settled difference',
  supplier_advance: 'Supplier advance', supplier_advance_apply: 'Supplier advance applied',
  customer_advance: 'Customer advance', customer_advance_apply: 'Customer advance applied',
};
const srcLabel = (s: string) => SRC_LABEL[s] ?? s.replace(/_/g, ' ');

function IconChevron({ open }: { open: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.14s' }}>
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Manual entry modal ───────────────────────────────────────────────────

interface Row { accountId: string; debit: string; credit: string; memo: string }
const BLANK_ROW: Row = { accountId: '', debit: '', credit: '', memo: '' };

function JournalEntryModal({ editing, accounts, onClose }: {
  editing: JournalEntry | null; accounts: LedgerAccount[]; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const isEdit = !!editing;
  const [date, setDate] = useState(editing?.date ?? todayISO());
  const [note, setNote] = useState(editing?.note ?? '');
  const [rows, setRows] = useState<Row[]>(
    editing
      ? editing.lines.map(l => ({
          accountId: l.accountId,
          debit: l.debit ? String(l.debit) : '',
          credit: l.credit ? String(l.credit) : '',
          memo: l.memo ?? '',
        }))
      : [{ ...BLANK_ROW }, { ...BLANK_ROW }],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const sorted = useMemo(
    () => [...accounts].sort((a, b) => (a.code || 'zzz').localeCompare(b.code || 'zzz')),
    [accounts],
  );
  const totalDebit  = round2(rows.reduce((s, r) => s + (parseFloat(r.debit) || 0), 0));
  const totalCredit = round2(rows.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0));
  const diff = round2(totalDebit - totalCredit);
  const balanced = Math.abs(diff) < 0.005 && totalDebit > 0;

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  async function save() {
    if (!balanced) return setError('Debits and credits must be equal (and non-zero) before saving.');
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    const lines = rows
      .filter(r => r.accountId && ((parseFloat(r.debit) || 0) > 0 || (parseFloat(r.credit) || 0) > 0))
      .map(r => ({
        accountId: r.accountId,
        debit: parseFloat(r.debit) || 0,
        credit: parseFloat(r.credit) || 0,
        memo: r.memo.trim() || undefined,
      }));

    try {
      const counterRef = doc(db, 'counters', business.id);
      const ref = editing ? doc(db, 'journalEntries', editing.id) : doc(collection(db, 'journalEntries'));
      await runTransaction(db, async tx => {
        let entryNo = editing?.entryNo;
        if (!editing) {
          const cs = await tx.get(counterRef);
          const n = (cs.exists() ? (cs.data().journalEntry ?? 0) : 0) + 1;
          tx.set(counterRef, { journalEntry: n }, { merge: true });
          entryNo = `${settings.journalPrefix ?? 'JE'}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`;
        }
        const entry = buildEntry({
          businessId: business.id, sourceType: 'journal', date,
          entryNo, currency: settings.currency, createdBy: user!.id,
          note: note.trim() || undefined, lines,
        });
        tx.set(ref, stripUndef({ ...entry, id: ref.id }));
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the entry.');
    }
    setSaving(false);
  }

  const money = (v: number) => fmtMoney(v, settings.currency);

  return (
    <Modal
      open onClose={onClose} size="lg"
      title={isEdit ? `Edit ${editing.entryNo ?? 'Journal Entry'}` : 'New Journal Entry'}
      subtitle={
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Manual posting
        </span>
      }
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <span style={{ fontSize: 12, fontFamily: 'var(--font-num)', color: balanced ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
            {balanced ? '✓ Balanced' : `Difference ${money(diff)}`}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving} disabled={!balanced}>{isEdit ? 'Save' : 'Post Entry'}</Button>
          </div>
        </div>
      }
    >
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="form-row-2" style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)' }}>
          Date
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)}
            style={{ display: 'block', marginTop: 4, width: '100%' }} />
        </label>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)' }}>
          Note (optional)
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="What is this for?"
            style={{ display: 'block', marginTop: 4, width: '100%' }} />
        </label>
      </div>

      <div className="table-wrap" style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}>
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th style={{ width: 120, textAlign: 'right' }}>Debit</th>
              <th style={{ width: 120, textAlign: 'right' }}>Credit</th>
              <th style={{ width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>
                  <select value={r.accountId} onChange={e => setRow(i, { accountId: e.target.value })} style={{ width: '100%' }}>
                    <option value="">Select…</option>
                    {(['asset', 'liability', 'equity', 'income', 'expense'] as LedgerAccountType[]).map(t => {
                      const opts = sorted.filter(a => a.type === t);
                      return opts.length ? (
                        <optgroup key={t} label={TYPE_LABEL[t]}>
                          {opts.map(a => <option key={a.id} value={a.id}>{a.code ? `${a.code} — ` : ''}{a.name}</option>)}
                        </optgroup>
                      ) : null;
                    })}
                  </select>
                </td>
                <td>
                  <input type="number" step="0.01" min="0" value={r.debit} className="no-spinner"
                    onChange={e => setRow(i, { debit: e.target.value, credit: e.target.value ? '' : r.credit })}
                    style={{ width: '100%', textAlign: 'right' }} />
                </td>
                <td>
                  <input type="number" step="0.01" min="0" value={r.credit} className="no-spinner"
                    onChange={e => setRow(i, { credit: e.target.value, debit: e.target.value ? '' : r.debit })}
                    style={{ width: '100%', textAlign: 'right' }} />
                </td>
                <td>
                  {rows.length > 2 && (
                    <button type="button" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16 }}>×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--surface-2)' }}>
              <td style={{ fontWeight: 700, fontSize: 12 }}>Total</td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12 }}>{money(totalDebit)}</td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12 }}>{money(totalCredit)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <button type="button" onClick={() => setRows(rs => [...rs, { ...BLANK_ROW }])}
        className="line-item-link-btn" style={{ marginTop: 8 }}>+ Add line</button>
    </Modal>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function JournalEntriesPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const { entries, accounts, loading } = useLedgerData();

  const [dateKey, setDateKey]       = useState<DateFilterKey>('this-month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [expanded, setExpanded]     = useState<Set<string>>(new Set());
  const [modal, setModal]           = useState<'new' | { edit: JournalEntry } | null>(null);
  const [confirmDel, setConfirmDel] = useState<JournalEntry | null>(null);
  const [busy, setBusy]             = useState(false);

  const nameById = useMemo(() => {
    const m: Record<string, string> = {};
    accounts.forEach(a => { m[a.id] = a.name; });
    return m;
  }, [accounts]);

  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const rows = useMemo(() => {
    if (!window) return [];
    return entries
      .filter(e => inWindow(e.date, window))
      .sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id));
  }, [entries, window]);
  // 50 rows at a time; a new filter goes back to page 1.
  const pg = usePaging(rows, JSON.stringify(window));

  const toggle = (id: string) =>
    setExpanded(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function doDelete(e: JournalEntry) {
    setBusy(true);
    const db = getClientDb();
    if (!db) { setBusy(false); return; }
    try {
      await runTransaction(db, async tx => { tx.delete(doc(db, 'journalEntries', e.id)); });
    } finally { setBusy(false); setConfirmDel(null); }
  }

  return (
    <AppLayout
      title="Journal Entries"
      subtitle="Every posting the books have made, newest first. Manual entries can be edited here."
      toolbar={<>
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
      </>}
      headerActions={
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setModal('new')} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>New Entry</Button>
      }
    >
      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : !window ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px', fontSize: 13, color: 'var(--text-3)' }}>
          Choose a From and To date above.
        </div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No entries in this period</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Change the date range, or run <strong>Settings → Accounting → Rebuild Now</strong>.
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 30 }}></th>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 160 }}>Type</th>
                  <th>Note</th>
                  <th style={{ textAlign: 'right', width: 140 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {pg.pageRows.map(e => {
                  const amount = e.lines.reduce((s, l) => s + (l.debit || 0), 0);
                  const open = expanded.has(e.id);
                  const manual = e.sourceType === 'journal';
                  return (
                    <Fragment key={e.id}>
                      <tr onClick={() => toggle(e.id)} style={{ cursor: 'pointer' }}>
                        <td style={{ color: 'var(--text-3)' }}><IconChevron open={open} /></td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{e.date}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                          {srcLabel(e.sourceType)}
                          {e.entryNo && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--font-num)' }}>{e.entryNo}</span>}
                          {e.isReversal && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--danger)' }}>reversal</span>}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{e.note || <span style={{ opacity: 0.4 }}>—</span>}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', textAlign: 'right', fontWeight: 600 }}>{money(amount)}</td>
                      </tr>
                      {open && (
                        <tr>
                          <td></td>
                          <td colSpan={4} style={{ padding: 0 }}>
                            <div style={{ background: 'var(--surface-2)', padding: '6px 0' }}>
                              {e.lines.map((l, i) => (
                                <div key={i} style={{
                                  display: 'grid', gridTemplateColumns: '1fr 120px 120px',
                                  padding: '5px 14px', fontSize: 12, color: 'var(--text-2)',
                                }}>
                                  <span>{nameById[l.accountId] ?? l.accountId}</span>
                                  <span style={{ fontFamily: 'var(--font-num)', textAlign: 'right' }}>{l.debit ? money(l.debit) : ''}</span>
                                  <span style={{ fontFamily: 'var(--font-num)', textAlign: 'right' }}>{l.credit ? money(l.credit) : ''}</span>
                                </div>
                              ))}
                              {manual && (
                                <div style={{ display: 'flex', gap: 8, padding: '6px 14px 4px' }}>
                                  <Button variant="secondary" size="sm" onClick={() => setModal({ edit: e })}>Edit</Button>
                                  <DeleteIconButton onClick={() => setConfirmDel(e)} />
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Paging page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} noun="entry" />
        </div>
      )}

      {modal === 'new' && <JournalEntryModal editing={null} accounts={accounts} onClose={() => setModal(null)} />}
      {modal && typeof modal === 'object' && 'edit' in modal && (
        <JournalEntryModal editing={modal.edit} accounts={accounts} onClose={() => setModal(null)} />
      )}
      {confirmDel && (
        <ConfirmModal
          open title="Delete this journal entry?"
          message={`${confirmDel.entryNo ?? 'This manual entry'} will be removed from the books.`}
          confirmLabel="Delete" loading={busy}
          onConfirm={() => doDelete(confirmDel)} onClose={() => setConfirmDel(null)}
        />
      )}
    </AppLayout>
  );
}
